import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import { useTheme } from '@mui/material/styles';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Checkbox } from '../Checkbox';
import {
  StyledTable,
  StyledTableContainer,
  StyledHeaderTableCell,
  StyledTableRow,
  StyledTableBodyCell,
  StyledTableFooterCell,
  MobileCardContainer,
  MobileCard,
  MobileCardRow,
  MobileCardLabel,
  MobileCardValue,
} from './Table.styled';
import { TableProps, CustomTableCellProps, CustomTableRowProps } from './Table.types';
import { TablePagination } from './TablePagination';
// Custom Table Cell Component
const CustomTableCell: React.FC<CustomTableCellProps> = ({
  cellVariant = 'body',
  customStyles,
  children,
  style,
  ...props
}) => {
  if (cellVariant === 'header') {
    return (
      <StyledHeaderTableCell {...props} style={{ ...customStyles, ...style }}>
        {children}
      </StyledHeaderTableCell>
    );
  }
  return (
    <StyledTableBodyCell {...props} style={{ ...customStyles, ...style }}>
      {children}
    </StyledTableBodyCell>
  );
};
// Custom Table Row Component
const CustomTableRow: React.FC<CustomTableRowProps> = ({ isSelected = false, customStyles, children, ...props }) => {
  return (
    <StyledTableRow {...props} selected={isSelected} style={customStyles}>
      {children}
    </StyledTableRow>
  );
};
const Table = <T extends Record<string, any>>({
  data = [],
  renderRow,
  mobileRowRender,
  headerRows = [],
  selectedRows = [],
  showHeader = true,
  stickyHeader = false,
  maxHeight,
  customCellStyles,
  pagination,
  collapses = false,
  children,
  headerContent,
  hasLeadingCheckboxes = false,
  onRowCheck,
  checkedRows = [],
  empty,
  isLoading = false,
  loadingRowsCount = 3,
  onRowClick,
  isExpandableTableVariant = false,
  ...props
}: TableProps<T>) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // Handle checkbox selection
  const handleRowCheckboxChange = (rowIndex: number, checked: boolean) => {
    if (onRowCheck) {
      const newCheckedRows = checked ? [...checkedRows, rowIndex] : checkedRows.filter((index) => index !== rowIndex);
      onRowCheck(newCheckedRows);
    }
  };
  // Handle select all checkbox
  const handleSelectAllChange = (checked: boolean) => {
    if (onRowCheck) {
      const newCheckedRows = checked ? data.map((_, index) => index) : [];
      onRowCheck(newCheckedRows);
    }
  };
  // Determine select all checkbox state
  const isAllSelected = data.length > 0 && checkedRows.length === data.length;
  const isIndeterminate = checkedRows.length > 0 && checkedRows.length < data.length;
  // Get labels from the last data header row, or generate default labels
  const getLabels = (): string[] => {
    const dataHeaderRow = headerRows.find((row) => row.type === 'data' || !row.type);
    if (dataHeaderRow?.labels) {
      return dataHeaderRow.labels;
    }
    if (dataHeaderRow?.cells) {
      return dataHeaderRow.cells.map((cell, index) => {
        if (typeof cell === 'string') {
          return cell;
        }
        return `Column ${index + 1}`;
      });
    }
    // For generic tables, we'll use object keys as labels if available
    if (data.length > 0) {
      return Object.keys(data[0]);
    }
    return [];
  };
  const labels = getLabels();
  // Function to render skeleton rows
  const renderSkeletonRows = (numRows: number) => {
    const numColumns = labels.length + (hasLeadingCheckboxes ? 1 : 0);
    return Array.from({ length: numRows }, (_, rowIndex) => (
      <CustomTableRow key={`skeleton-${rowIndex}`}>
        {Array.from({ length: numColumns }, (_, cellIndex) => (
          <CustomTableCell key={`skeleton-${rowIndex}-${cellIndex}`} customStyles={customCellStyles}>
            <Skeleton data-testid="skeleton" variant="text" animation="wave" width="80%" />
          </CustomTableCell>
        ))}
      </CustomTableRow>
    ));
  };
  // Function to render skeleton mobile cards
  const renderSkeletonMobileCards = (numCards: number) => {
    return Array.from({ length: numCards }, (_, cardIndex) => (
      <MobileCard key={`skeleton-mobile-${cardIndex}`}>
        {Array.from({ length: labels.length + (hasLeadingCheckboxes ? 1 : 0) }, (_, rowIndex) => (
          <MobileCardRow key={rowIndex}>
            <MobileCardLabel>
              <Skeleton variant="text" animation="wave" width="60%" />
            </MobileCardLabel>
            <MobileCardValue>
              <Skeleton variant="text" animation="wave" width="80%" />
            </MobileCardValue>
          </MobileCardRow>
        ))}
      </MobileCard>
    ));
  };
  // Default renderRow function when none is provided
  const defaultRenderRow = (item: T, rowIndex: number) => {
    const dataHeaderRow = headerRows.find((row) => row.type === 'data' || !row.type);
    if (dataHeaderRow?.cells) {
      // Use the order from header cells
      const dataCells = dataHeaderRow.cells.map((cell, cellIndex) => {
        const key = typeof cell === 'string' ? cell : `column-${cellIndex}`;
        // Try to find the property with case-insensitive matching
        let value: any;
        if (typeof key === 'string') {
          // First try exact match
          if (key in item) {
            value = item[key as keyof T];
          } else {
            // Try case-insensitive match
            const objectKeys = Object.keys(item);
            const matchedKey = objectKeys.find((k) => k.toLowerCase() === key.toLowerCase());
            if (matchedKey) {
              value = item[matchedKey as keyof T];
            } else {
              // Fallback to undefined if no match found
              value = undefined;
            }
          }
        } else {
          value = item[key as keyof T];
        }
        return (
          <CustomTableCell key={`${rowIndex}-${cellIndex}`} customStyles={customCellStyles}>
            {value}
          </CustomTableCell>
        );
      });
      return <>{dataCells}</>;
    }
    // Fallback to object keys order
    const dataCells = Object.entries(item).map(([, value], cellIndex) => (
      <CustomTableCell key={`${rowIndex}-${cellIndex}`} customStyles={customCellStyles}>
        {value}
      </CustomTableCell>
    ));
    return <>{dataCells}</>;
  };
  // Use provided renderRow or default, but always include checkbox if hasLeadingCheckboxes is true
  const renderRowFunction = (item: T, rowIndex: number) => {
    const checkboxCell = hasLeadingCheckboxes ? (
      <CustomTableCell key={`${rowIndex}-checkbox`} customStyles={customCellStyles}>
        <Checkbox
          checked={checkedRows.includes(rowIndex)}
          onChange={(e) => handleRowCheckboxChange(rowIndex, e.target.checked)}
          size="small"
        />
      </CustomTableCell>
    ) : null;
    if (renderRow) {
      // If custom renderRow is provided, wrap it with checkbox
      return (
        <>
          {checkboxCell}
          {renderRow(item, rowIndex)}
        </>
      );
    } else {
      // Use default rendering with checkbox
      const defaultCells = defaultRenderRow(item, rowIndex);
      return (
        <>
          {checkboxCell}
          {defaultCells}
        </>
      );
    }
  };
  // Default mobile row render function
  const defaultMobileRowRender = (item: T, rowIndex: number, labels: string[]) => {
    const dataHeaderRow = headerRows.find((row) => row.type === 'data' || !row.type);
    // Add checkbox row if hasLeadingCheckboxes is true
    const checkboxRow = hasLeadingCheckboxes ? (
      <MobileCardRow key="checkbox">
        <MobileCardLabel>Select</MobileCardLabel>
        <MobileCardValue>
          <Checkbox
            checked={checkedRows.includes(rowIndex)}
            onChange={(e) => handleRowCheckboxChange(rowIndex, e.target.checked)}
            size="small"
          />
        </MobileCardValue>
      </MobileCardRow>
    ) : null;
    if (dataHeaderRow?.cells) {
      // Use the order from header cells
      const dataRows = dataHeaderRow.cells.map((cell, cellIndex) => {
        const key = typeof cell === 'string' ? cell : `column-${cellIndex}`;
        // Try to find the property with case-insensitive matching
        let value: any;
        if (typeof key === 'string') {
          // First try exact match
          if (key in item) {
            value = item[key as keyof T];
          } else {
            // Try case-insensitive match
            const objectKeys = Object.keys(item);
            const matchedKey = objectKeys.find((k) => k.toLowerCase() === key.toLowerCase());
            if (matchedKey) {
              value = item[matchedKey as keyof T];
            } else {
              value = undefined;
            }
          }
        } else {
          value = item[key as keyof T];
        }
        return (
          <MobileCardRow key={cellIndex}>
            <MobileCardLabel>{labels[cellIndex] || key}</MobileCardLabel>
            <MobileCardValue
              sx={{
                color: cellIndex === 0 ? theme.palette.text.primary : '#475467',
                fontSize: cellIndex === 0 ? '16px' : '14px',
              }}
            >
              {value}
            </MobileCardValue>
          </MobileCardRow>
        );
      });
      return (
        <>
          {checkboxRow}
          {dataRows}
        </>
      );
    }
    // Fallback to object keys order
    const dataRows = Object.entries(item).map(([key, value], cellIndex) => (
      <MobileCardRow key={cellIndex}>
        <MobileCardLabel>{labels[cellIndex] || key}</MobileCardLabel>
        <MobileCardValue
          sx={{
            color: cellIndex === 0 ? theme.palette.text.primary : '#475467',
            fontSize: cellIndex === 0 ? '16px' : '14px',
          }}
        >
          {value}
        </MobileCardValue>
      </MobileCardRow>
    ));
    return (
      <>
        {checkboxRow}
        {dataRows}
      </>
    );
  };
  // Use provided mobileRowRender or default
  const mobileRowRenderFunction = mobileRowRender || defaultMobileRowRender;
  // Render mobile cards
  if (collapses && isMobile) {
    return (
      <Paper
        sx={{
          mt: 0,
          mb: 0,
          borderRadius: '12px',
          border: '1px solid #EAECF0',
          boxShadow: '0px 1px 2px 0px #1018280D',
        }}
      >
        {headerContent}
        <Box>
          {isLoading ? (
            <MobileCardContainer>{renderSkeletonMobileCards(loadingRowsCount)}</MobileCardContainer>
          ) : data.length === 0 && empty ? (
            <Box sx={{ p: 3 }}>{empty}</Box>
          ) : (
            <>
              <MobileCardContainer>
                {data.map((item, rowIndex) => (
                  <MobileCard key={rowIndex} className={selectedRows.includes(rowIndex) ? 'selected' : ''}>
                    {mobileRowRenderFunction(item, rowIndex, labels)}
                  </MobileCard>
                ))}
              </MobileCardContainer>
              {pagination?.showPagination && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                  <TablePagination
                    disableNext={pagination.disableNext}
                    disablePrevious={pagination.disablePrevious}
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    onPageChange={pagination.onPageChange}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Paper>
    );
  }
  // Render desktop table
  return (
    <Paper
      sx={{
        mt: 0,
        mb: 0,
        borderRadius: isExpandableTableVariant ? '0px' : '12px',
        border: isExpandableTableVariant ? 'none' : '1px solid #EAECF0',
        borderTop: isExpandableTableVariant ? '1px solid #EAECF0' : undefined,
        boxShadow: '0px 1px 2px 0px #1018280D',
        width: '100%',
      }}
    >
      {headerContent && (
        <Box
          sx={{
            borderBottom: '1px solid #EAECF0',
            borderRadius: isExpandableTableVariant ? '0px' : '12px 12px 0 0',
            background: 'white',
          }}
        >
          {headerContent}
        </Box>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <StyledTableContainer
          hasHeaderContent={!!headerContent}
          style={{
            maxHeight,
            borderRadius: isExpandableTableVariant ? '0px' : undefined,
          }}
        >
          <StyledTable stickyHeader={stickyHeader} {...props}>
            {showHeader && (headerRows.length > 0 || labels.length > 0) && (
              <TableHead>
                {headerRows.length > 0 ? (
                  headerRows.map((headerRow, rowIndex) => (
                    <TableRow key={rowIndex}>
                      {/* Add checkbox header cell if hasLeadingCheckboxes is true */}
                      {hasLeadingCheckboxes && rowIndex === 0 && (
                        <CustomTableCell
                          key="checkbox-header"
                          cellVariant="header"
                          customStyles={{ ...customCellStyles, ...headerRow.customStyles }}
                        >
                          <Checkbox
                            checked={isAllSelected}
                            indeterminate={isIndeterminate}
                            onChange={(e) => handleSelectAllChange(e.target.checked)}
                            size="small"
                          />
                        </CustomTableCell>
                      )}
                      {headerRow.cells.map((cell, cellIndex) => (
                        <CustomTableCell
                          key={cellIndex}
                          cellVariant="header"
                          colSpan={headerRow.colSpan?.[cellIndex]}
                          customStyles={{
                            ...customCellStyles,
                            ...headerRow.customStyles,
                            textWrap: isExpandableTableVariant ? 'nowrap' : undefined,
                          }}
                        >
                          {cell}
                        </CustomTableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    {/* Add checkbox header cell if hasLeadingCheckboxes is true */}
                    {hasLeadingCheckboxes && (
                      <CustomTableCell key="checkbox-header" cellVariant="header" customStyles={customCellStyles}>
                        <Checkbox
                          checked={isAllSelected}
                          indeterminate={isIndeterminate}
                          onChange={(e) => handleSelectAllChange(e.target.checked)}
                          size="small"
                        />
                      </CustomTableCell>
                    )}
                    {labels.map((label, cellIndex) => (
                      <CustomTableCell key={cellIndex} cellVariant="header" customStyles={customCellStyles}>
                        {label}
                      </CustomTableCell>
                    ))}
                  </TableRow>
                )}
              </TableHead>
            )}
            <TableBody>
              {isLoading ? (
                renderSkeletonRows(loadingRowsCount)
              ) : data.length === 0 && empty ? (
                <TableRow>
                  <CustomTableCell colSpan={labels.length + (hasLeadingCheckboxes ? 1 : 0)}>{empty}</CustomTableCell>
                </TableRow>
              ) : (
                <>
                  {data.map((item, rowIndex) => (
                    <CustomTableRow
                      key={rowIndex}
                      isSelected={selectedRows.includes(rowIndex)}
                      onClick={() => onRowClick?.(item, rowIndex)}
                    >
                      {renderRowFunction(item, rowIndex)}
                    </CustomTableRow>
                  ))}
                  {children}
                </>
              )}
            </TableBody>
          </StyledTable>
        </StyledTableContainer>
        {pagination && (
          <Box
            sx={{
              borderTop: '1px solid #EAECF0',
              padding: '16px 24px',
              borderRadius: '0 0 12px 12px',
              display: 'flex',
              justifyContent: 'flex-end',
              backgroundColor: 'white',
            }}
          >
            <TablePagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={pagination.onPageChange}
              disableNext={pagination.disableNext}
              disablePrevious={pagination.disablePrevious}
            />
          </Box>
        )}
      </Box>
    </Paper>
  );
};
// Export components
export {
  Table,
  CustomTableCell,
  CustomTableRow,
  TablePagination,
  // Styled components
  StyledTable,
  StyledTableContainer,
  StyledHeaderTableCell,
  StyledTableRow,
  StyledTableBodyCell,
  StyledTableFooterCell,
  MobileCardContainer,
  MobileCard,
  MobileCardRow,
  MobileCardLabel,
  MobileCardValue,
};
// Re-export types
export type { TableProps, CustomTableCellProps, CustomTableRowProps, PaginationProps, HeaderRow } from './Table.types';
